import { type CustomFormApi, type CustomFormState, Form } from 'informed';
import { Payment, Form as PaymentForm, type Results } from 'payments-lib-types';
import {
  type FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { useSWRConfig } from 'swr';
import { useShallow } from 'zustand/react/shallow';
import { Button, ButtonClose } from '@/components/buttons';
import { ScNotFound } from '@/components/Deposit/styled';
import { IconComponent } from '@/components/IconComponent';
import { ImageComponent } from '@/components/Image';
import { Modal } from '@/components/Modal';
import {
  PaymentMethodSteps,
  type ProfileInfoConfig,
} from '@/components/payments/profileInfo';
import { PLAYER_ACCOUNTS_KEY } from '@/helpers/constants';
import { amountWithCurrencySymbol } from '@/helpers/currency';
import { ThemeMode } from '@/helpers/enums';
import {
  changeFieldNamesForMethods,
  FieldsType,
  formattingData,
  formattingWithdrawData,
  getCardLastNumbers,
  getDetailedPaymentsErrorText,
  hideSensitiveData,
  type IMethodFormProps,
  type MethodFormApi2,
  methodFieldsPath,
} from '@/helpers/payment';
import {
  deleteSavedPaymentMethod,
  getMethodFields,
  submitFormPayment,
} from '@/helpers/payments';
import { showTooltip } from '@/helpers/tooltip';
import { unmaskAmount } from '@/helpers/validators';
import { useSwrMutation } from '@/hooks/swr';
import { AMOUNT_VALUE_FIELD } from '@/modules/deposit';
import {
  sendWithdrawProfileInfoStepAppearanceEvent,
  sendWithdrawProfileInfoStepResultEvent,
} from '@/modules/googleAnalytics';
import { isPlayerPaymentsPageKey } from '@/modules/profile/hooks/usePlayerPaymentsPage';
import {
  type PaymentMethod as PaymentMethodI,
  STORAGE,
  useGetCommonStore,
  useLocalStorageStore,
  useModalsStore,
  useWithdrawStore,
} from '@/store';
import { REASON_FOR_UPDATING_USER } from '@/store/commonStore/slices/auth/helpers';
import { PersistentFormName } from '@/store/commonStore/slices/persistant/usePersistentFormsSlice';
import { TooltipTypes } from '@/store/tooltipStore';
import { Spinner } from '@/styles/common';
import { PaymentField } from './fields';
import { hasErrors } from './helpers/hasErrors';
import {
  ScConfirmRemoveModal,
  ScCurrentPaymentMethod,
  ScPaymentInfoRow,
} from './styled';

const withdrawProfileConfig: ProfileInfoConfig = {
  paymentAction: Payment.Action.Cashout,
  persistFormName: PersistentFormName.WithdrawPersonalInfo,
  saveKey: 'savePlayerFieldsWithdraw',
  fetchUserReason: REASON_FOR_UPDATING_USER.DEPOSIT,
  sendAppearanceEvent: sendWithdrawProfileInfoStepAppearanceEvent,
  sendResultEvent: sendWithdrawProfileInfoStepResultEvent,
};

export const PaymentMethod: FC = () => {
  const { mainColor, gray1Color, grayDarkColor } = useTheme();
  const useCommonStore = useGetCommonStore();

  const user = useCommonStore.use.user();
  const currentPaymentMethod = useWithdrawStore.use.currentPaymentMethod();
  const activeThemeMode = useWithdrawStore.use.activeThemeMode();

  const {
    id,
    title,
    termsOfService: {
      restrictions: { amountCurrencyCode: currency },
    },
    savedProfileDetails,
    isPrevUsed,
  } = currentPaymentMethod || ({} as PaymentMethodI);

  const {
    trigger: fetchMethodFields,
    data: methodFieldsData,
    isMutating: methodFieldsPending,
  } = useSwrMutation<Results.GetMethodFieldsResult>({
    key: 'methodFields',
    fetcher: getMethodFields,
  });

  useEffect(() => {
    if (!user?.currency) return;

    fetchMethodFields({
      currency: user.currency,
      id,
      savedProfileId:
        isPrevUsed && savedProfileDetails ? savedProfileDetails.id : undefined,
      paymentAction: Payment.Action.Cashout,
    });
  }, [fetchMethodFields, id, user?.currency, isPrevUsed, savedProfileDetails]);

  if (methodFieldsData?.playerFields?.length) {
    return (
      <ScCurrentPaymentMethod mode={activeThemeMode}>
        <PaymentMethodSteps
          config={withdrawProfileConfig}
          methodFields={methodFieldsData}
          mode={activeThemeMode}
          paymentMethod={currentPaymentMethod}
          renderPaymentInfo={() => (
            <MethodForm
              currency={currency}
              id={id}
              isPrevUsed={isPrevUsed}
              savedProfileDetails={savedProfileDetails}
              {...methodFieldsData}
            />
          )}
        />
      </ScCurrentPaymentMethod>
    );
  }

  return (
    <ScCurrentPaymentMethod mode={activeThemeMode}>
      <h3 className="payment-title">{title}</h3>
      {methodFieldsPending || methodFieldsData === undefined ? (
        <Spinner
          background={
            activeThemeMode === ThemeMode.Dark ? grayDarkColor : gray1Color
          }
          color={mainColor}
          margin="16px auto"
          size={30}
        />
      ) : (
        <MethodForm
          currency={currency}
          id={id}
          isPrevUsed={isPrevUsed}
          savedProfileDetails={savedProfileDetails}
          {...methodFieldsData}
        />
      )}
    </ScCurrentPaymentMethod>
  );
};

const MethodForm: FC<IMethodFormProps> = ({
  id,
  currency,
  methodFields,
  savedProfileDetails,
  amountField,
  isSubmitAvailable,
  isPrevUsed,
}) => {
  const { t } = useTranslation(['deposits', 'withdraw']);

  const { mutate } = useSWRConfig();

  const close = useModalsStore.use.close();
  const useCommonStore = useGetCommonStore();

  const user = useCommonStore.use.user();
  const fetchUser = useCommonStore.use.fetchUser();
  const { get, push } = useLocalStorageStore(
    useShallow((state) => ({
      get: state.get,
      push: state.push,
    }))
  );
  const { currentPaymentMethod, currentAmount } = useWithdrawStore(
    useShallow((state) => ({
      currentPaymentMethod: state.currentPaymentMethod,
      currentAmount: state.currentAmount,
    }))
  );

  const [isConfirmRemoveModalOpen, toggleConfirmRemoveModal] = useState(false);

  const formApiRef = useRef<CustomFormApi<MethodFormApi2>>();

  const {
    trigger: fetchSubmitFormPayment,
    data: submitFormPaymentData,
    isMutating: submitFormPaymentPending,
  } = useSwrMutation({
    key: 'submitFormPayment',
    fetcher: submitFormPayment,
  });

  const initialValues = useMemo(() => {
    const payments = get<{
      [id: string]: MethodFormApi2;
    } | null>(STORAGE.PAYMENTS);

    if (!(payments && formApiRef)) return;

    const currentPayment = payments[id];

    if (!currentPayment) return;

    return currentPayment;
  }, [id, get]);

  useEffect(() => {
    if (!submitFormPaymentData) return;
    console.log('[PAYMENTS-LIB] response:', submitFormPaymentData);
    const { statusCode } = submitFormPaymentData;
    // handle case when response has no status code field, but has type params_error.
    const isResponseWithErrorText =
      hasErrors(submitFormPaymentData.amountFieldErrors) ||
      hasErrors(submitFormPaymentData.commonErrors) ||
      hasErrors(submitFormPaymentData.methodFieldsErrors);
    if (statusCode === undefined && !isResponseWithErrorText) {
      mutate(isPlayerPaymentsPageKey);
      mutate(PLAYER_ACCOUNTS_KEY);
      fetchUser(REASON_FOR_UPDATING_USER.DEPOSIT);
      close();
      return;
    }

    if (submitFormPaymentData.type === 'params_error') {
      return getDetailedPaymentsErrorText(
        submitFormPaymentData,
        t,
        user?.currency,
        isResponseWithErrorText
      );
    }
  }, [submitFormPaymentData, t, user?.currency, close, mutate, fetchUser]);

  const handleChange = useCallback(
    (values: CustomFormState<MethodFormApi2>) => {
      const filteredValues = hideSensitiveData([
        { fieldPath: methodFieldsPath.cvvPath, toRemove: true },
        { fieldPath: methodFieldsPath.cvvField, toRemove: true },
        { fieldPath: methodFieldsPath.amountValue, toRemove: true },
      ])(values.values) as MethodFormApi2;

      push(STORAGE.PAYMENTS, { [id]: filteredValues });
    },
    [id, push]
  );

  const handleSubmit = useCallback(
    async (values: MethodFormApi2) => {
      try {
        const updatedValues = {
          ...values,
          amountValue: unmaskAmount(values.amountValue),
          ...(isPrevUsed &&
            savedProfileDetails?.id && {
              savedProfileId: savedProfileDetails.id,
            }),
        };
        const formattedValues = formattingData(updatedValues);
        const formattedWithdrawValues = formattingWithdrawData(
          formattedValues,
          user?.country
        );
        const data = { id, currency, ...formattedWithdrawValues };

        await fetchSubmitFormPayment({
          ...data,
          paymentAction: Payment.Action.Cashout,
        }).then((data: { type: string }) => {
          if (data.type === 'params_error') return;
          mutate(isPlayerPaymentsPageKey);
          mutate(PLAYER_ACCOUNTS_KEY);
          close();
          showTooltip({
            type: TooltipTypes.INFO,
            body: (
              <div>
                <p className="title">{t('withdraw:notification.title')}</p>
                <p>{t('withdraw:notification.body')}</p>
              </div>
            ),
          });
          fetchUser(REASON_FOR_UPDATING_USER.REGISTRATION);
        });
      } catch (error) {
        console.log(error);
      }
    },
    [
      isPrevUsed,
      savedProfileDetails?.id,
      id,
      currency,
      fetchSubmitFormPayment,
      close,
      t,
      fetchUser,
    ]
  );

  const fieldsByType = useMemo(() => {
    if (!methodFields || methodFields.length === 0) return {};

    return methodFields.reduce(
      (list, field) => {
        if (
          [
            PaymentForm.MethodFieldType.Cvv,
            PaymentForm.MethodFieldType.CardExpiryDate,
          ].includes(field.type)
        ) {
          const typeData = list[FieldsType.CARD_DETAILS] || [];

          return {
            ...list,
            [FieldsType.CARD_DETAILS]: [...typeData, field],
          };
        }
        if (field.fieldName) {
          const typeData = list[FieldsType.FORM] || [];
          return {
            ...list,
            [FieldsType.FORM]: [...typeData, field],
          };
        }
        const typeData = list[FieldsType.TEXT] || [];

        return {
          ...list,
          [FieldsType.TEXT]: [...typeData, field],
        };
      },
      {} as { [key: string]: PaymentForm.MethodField[] }
    );
  }, [methodFields]);

  const isInfoBlockAllowed = useMemo(() => {
    if (!currentPaymentMethod) return false;
    return [
      Payment.MethodType.Cards,
      Payment.MethodType.Direct,
      Payment.MethodType.BankTransfer,
    ].includes(currentPaymentMethod.type);
  }, [currentPaymentMethod]);

  if (!currentPaymentMethod) {
    return <ScNotFound>{t('deposits:notSelectedMethod')}</ScNotFound>;
  }

  return (
    <>
      <Form<MethodFormApi2>
        formApiRef={formApiRef}
        initialValues={initialValues}
        onChange={handleChange}
        onSubmit={({ values }) => handleSubmit(values)}
      >
        {amountField && (
          <PaymentField
            {...({
              ...amountField,
              fieldName: AMOUNT_VALUE_FIELD,
            } as PaymentForm.MethodField)}
          />
        )}
        {methodFields ? (
          <>
            {!!fieldsByType[FieldsType.FORM]?.length &&
              changeFieldNamesForMethods(fieldsByType[FieldsType.FORM]).map(
                (field, index) => <PaymentField key={index} {...field} />
              )}

            {!!fieldsByType[FieldsType.CARD_DETAILS]?.length && (
              <div className="card-details">
                {changeFieldNamesForMethods(
                  fieldsByType?.[FieldsType.CARD_DETAILS]
                ).map((field, index) => (
                  <PaymentField key={index} {...field} />
                ))}
              </div>
            )}

            {!!fieldsByType[FieldsType.TEXT]?.length &&
              fieldsByType?.[FieldsType.TEXT].map((field, index) => (
                <PaymentField key={index} {...field} />
              ))}
          </>
        ) : (
          <ScNotFound>{t('withdraw:tryOtherMethod')}</ScNotFound>
        )}
        {isInfoBlockAllowed && (
          <ScPaymentInfoRow>
            <IconComponent name="tooltipIcon" />
            <div>
              <span>{t('withdraw:banners.banner-2')}</span>
            </div>
          </ScPaymentInfoRow>
        )}
        <ScPaymentInfoRow>
          <IconComponent name="tooltipIcon" />
          <div>
            <span>{t('withdraw:banners.banner-1')}</span>
          </div>
        </ScPaymentInfoRow>
        {isSubmitAvailable && (
          <div className="deposit-button">
            <Button
              isLoading={submitFormPaymentPending}
              primary
              type="submit"
              wide
            >
              {t('withdraw:withdraw')}{' '}
              {currentAmount
                ? amountWithCurrencySymbol(user?.currency, currentAmount)
                : null}
            </Button>
            {isPrevUsed && savedProfileDetails?.isRemoveAvailable && (
              <div
                className="remove-btn"
                onClick={() => toggleConfirmRemoveModal(true)}
              >
                {t('deposits:buttons.remove')}
              </div>
            )}
          </div>
        )}
      </Form>

      {isConfirmRemoveModalOpen && (
        <ConfirmRemoveMethodModal
          currency={currency}
          onClose={() => toggleConfirmRemoveModal(false)}
        />
      )}
    </>
  );
};

interface ConfirmRemoveMethodModalProps {
  currency: string;
  onClose: () => void;
}

const ConfirmRemoveMethodModal: FC<ConfirmRemoveMethodModalProps> = ({
  onClose,
  currency,
}) => {
  const { t } = useTranslation(['common', 'deposits']);
  const { whiteColor, mainColor } = useTheme();

  const { currentPaymentMethod, previouslyUsedPaymentMethods } =
    useWithdrawStore(
      useShallow((state) => ({
        currentPaymentMethod: state.currentPaymentMethod,
        previouslyUsedPaymentMethods: state.previouslyUsedPaymentMethods,
      }))
    );

  const [isMethodRemoved, setIsMethodRemoved] = useState(false);

  const { trigger: requestDeleteSavedMethod, isMutating: pending } =
    useSwrMutation({
      key: 'deleteSavedPaymentMethod',
      fetcher: deleteSavedPaymentMethod,
    });

  const [cardNumber, savedMethodId] = useMemo(() => {
    if (!currentPaymentMethod?.savedProfileDetails) return [];

    return [
      getCardLastNumbers(currentPaymentMethod.savedProfileDetails),
      currentPaymentMethod.savedProfileDetails.id,
    ];
  }, [currentPaymentMethod?.savedProfileDetails]);

  const handleDeleteSavedMethod = useCallback(async () => {
    try {
      if (!(currentPaymentMethod?.id && savedMethodId)) return;

      const { response } = await requestDeleteSavedMethod({
        id: currentPaymentMethod.id,
        currency,
        savedProfileId: savedMethodId,
      });

      if (response?.data) {
        showTooltip({
          type: TooltipTypes.ERROR,
          body: 'Oops! Something went wrong',
        });
      } else {
        setIsMethodRemoved(true);
      }
    } catch (error) {
      console.log(error);
    }
  }, [
    requestDeleteSavedMethod,
    savedMethodId,
    currentPaymentMethod?.id,
    currency,
  ]);

  const handleClose = useCallback(() => {
    if (isMethodRemoved) {
      const result = previouslyUsedPaymentMethods
        ? previouslyUsedPaymentMethods.filter(
            ({ savedProfileDetails }) =>
              savedProfileDetails?.id !== savedMethodId
          )
        : [];
      useWithdrawStore.setState({ previouslyUsedPaymentMethods: result });
    }
    onClose();
  }, [previouslyUsedPaymentMethods, onClose, isMethodRemoved, savedMethodId]);

  return (
    <Modal>
      <ScConfirmRemoveModal>
        <ButtonClose dark onClick={handleClose} right={12} top={12} />
        {pending ? (
          <Spinner
            background={whiteColor}
            color={mainColor}
            margin="16px auto"
            size={30}
          />
        ) : isMethodRemoved ? (
          <>
            <h3>{t('deposits:removeModal.successTitle')}</h3>
            <p>
              <Trans
                components={{ b: <b /> }}
                i18nKey="removeModal.success"
                ns="deposits"
                values={{ cardNumber }}
              />
            </p>
          </>
        ) : (
          <>
            <ImageComponent
              alt="emoji"
              height={80}
              src="/emojis/thinkingEmoji.webp"
              width={80}
            />
            <h3>{t('deposits:removeModal.title')}</h3>
            <p>
              <Trans
                components={{ b: <b /> }}
                i18nKey="removeModal.text"
                ns="deposits"
                values={{ cardNumber }}
              />
            </p>
            <footer>
              <Button
                autoPaddingHorizontal
                blackTransparent
                large
                onClick={handleDeleteSavedMethod}
                wide
              >
                {t('yes')}
              </Button>
              <Button
                autoPaddingHorizontal
                large
                onClick={onClose}
                primary
                wide
              >
                {t('no')}
              </Button>
            </footer>
          </>
        )}
      </ScConfirmRemoveModal>
    </Modal>
  );
};
